import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

import ProgressBar from "../wizard/ProgressBar";
import Footer from "../wizard/Footer";
import MemberSelection from "../wizard/steps/MemberSelection";
import InsuredDetails from "../wizard/steps/MemberDetails";
import ProposerContact from "../wizard/steps/LocationContact";
import BankDetails from "../wizard/steps/BankDetails";
import Review from "../wizard/steps/Review";
import KYC from "../wizard/steps/KYC";
import Success from "../wizard/steps/Success";

import {
  STORAGE_KEY,
  initialState,
  validateStep,
  validateSingleInsured,
  TOTAL_STEPS,
} from "../wizard/wizardUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1588979355313-6711a095465f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseXxlbnwwfHx8fDE3ODMxMTA0MzV8MA&ixlib=rb-4.1.0&q=85";

export default function Wizard() {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.step === TOTAL_STEPS) return { ...initialState };
        return { ...initialState, ...parsed };
      }
    } catch {
      /* ignore */
    }
    return initialState;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Inner pager index for the "Insured details" step (one member per screen).
  const [memberIdx, setMemberIdx] = useState(0);

  useEffect(() => {
    try {
      const toSave = { ...state };
      if (toSave.step === TOTAL_STEPS) return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch {
      /* ignore */
    }
  }, [state]);

  const step = state.step;

  const goNext = async () => {
    // Step 1 is an inner pager: validate + advance one member at a time.
    if (step === 1) {
      const members = state.selectedMembers;
      const mKey = members[Math.min(memberIdx, members.length - 1)];
      const errs = validateSingleInsured(state, mKey);
      setErrors(errs);
      if (Object.keys(errs).length) {
        toast.error("Please fix the highlighted fields.");
        return;
      }
      setErrors({});
      if (memberIdx < members.length - 1) {
        setMemberIdx((i) => i + 1);
      } else {
        setMemberIdx(0);
        setState((s) => ({ ...s, step: s.step + 1 }));
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const errs = validateStep(step, state);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    if (step === 5) {
      setSubmitting(true);
      try {
        const payload = {
          selectedMembers: state.selectedMembers,
          insured: state.insured,
          proposer: state.proposer,
          bank: state.bank,
          kyc: state.kyc,
        };
        const res = await axios.post(`${API}/submissions`, payload);
        setState((s) => ({
          ...s,
          step: TOTAL_STEPS,
          referenceNumber: res.data.referenceNumber,
        }));
        localStorage.removeItem(STORAGE_KEY);
        toast.success("Submission received");
      } catch (err) {
        console.error(err);
        const msg =
          err?.response?.data?.detail || "Something went wrong. Please try again.";
        toast.error(typeof msg === "string" ? msg : "Validation failed. Check inputs.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setErrors({});
    if (step === 0) setMemberIdx(0);
    setState((s) => ({ ...s, step: s.step + 1 }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setErrors({});
    // Within the member pager, step back through members first.
    if (step === 1 && memberIdx > 0) {
      setMemberIdx((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Returning into the member pager from the next step lands on the last member.
    if (step === 2) {
      setMemberIdx(Math.max(0, state.selectedMembers.length - 1));
    }
    setState((s) => ({ ...s, step: Math.max(0, s.step - 1) }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState({ ...initialState });
    setErrors({});
    setMemberIdx(0);
  };

  const stepView = useMemo(() => {
    switch (step) {
      case 0:
        return <MemberSelection state={state} setState={setState} errors={errors} />;
      case 1:
        return (
          <InsuredDetails
            state={state}
            setState={setState}
            errors={errors}
            activeIdx={memberIdx}
          />
        );
      case 2:
        return <ProposerContact state={state} setState={setState} errors={errors} />;
      case 3:
        return <BankDetails state={state} setState={setState} errors={errors} />;
      case 4:
        return <Review state={state} />;
      case 5:
        return <KYC state={state} setState={setState} errors={errors} />;
      case 6:
        return (
          <Success referenceNumber={state.referenceNumber} onReset={resetAll} />
        );
      default:
        return null;
    }
  }, [step, state, errors, memberIdx]);

  const continueLabel =
    step === 4 ? "Continue to Documents" : step === 5 ? "Submit Application" : "Continue";

  return (
    <div className="min-h-screen w-full">
      <div className="mx-auto grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        <aside className="hidden lg:flex lg:col-span-4 xl:col-span-5 relative bg-surface border-r border-subtle">
          <div className="absolute inset-0">
            <img
              src={HERO_IMG}
              alt=""
              className="w-full h-full object-cover opacity-70"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(249,249,247,0.5) 0%, rgba(249,249,247,0.92) 100%)",
              }}
            />
          </div>
          <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
            <div>
              <a
                href="/"
                className="inline-flex items-center gap-2 text-sm font-semibold"
                data-testid="brand-logo"
              >
                <span className="w-8 h-8 rounded-full accent-bg text-white flex items-center justify-center">
                  <ShieldCheck size={16} />
                </span>
                <span className="tracking-tight text-primary-brand text-lg">
                  PolicyWise
                </span>
              </a>
            </div>
            <div>
              <h1 className="text-4xl xl:text-5xl font-medium tracking-tight leading-[1.1] mb-4 text-primary-brand">
                Cover the people who matter, in six easy steps.
              </h1>
              <p className="text-secondary-brand max-w-md leading-relaxed">
                We ask only what&apos;s necessary. Your data stays private and is
                used solely for policy issuance.
              </p>
              <div className="mt-8 flex items-center gap-3 text-xs text-muted-brand">
                <span className="uppercase tracking-widest">256-bit secure</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="lg:col-span-8 xl:col-span-7 flex flex-col">
          <div className="flex-1 flex flex-col px-5 sm:px-8 lg:px-16 xl:px-24 py-8 sm:py-10">
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold">
                <span className="w-8 h-8 rounded-full accent-bg text-white flex items-center justify-center">
                  <ShieldCheck size={16} />
                </span>
                <span className="tracking-tight text-primary-brand text-lg">
                  PolicyWise
                </span>
              </a>
            </div>

            {step < TOTAL_STEPS && (
              <div className="mb-8">
                <ProgressBar step={step} />
              </div>
            )}

            <div className="flex-1 max-w-2xl w-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step === 1 ? `1-${memberIdx}` : step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {stepView}
                </motion.div>
              </AnimatePresence>
            </div>

            {step < TOTAL_STEPS && (
              <div className="mt-10 flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-between max-w-2xl w-full">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={step === 0 || submitting}
                  className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="back-button"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={submitting}
                  className="btn-primary"
                  data-testid="continue-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Submitting…
                    </>
                  ) : (
                    <>
                      {continueLabel}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="max-w-2xl w-full">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
